<!--
  系统设置面板。

  数据形态是「键 + 值 + 类型 + 分组」的 KV，所以界面不写死任何开关，
  一律按后端返回的 valueType 渲染控件：BOOL → 开关，其余 → 文本输入。
  后端新增一个开关（system_settings 插一行 + SystemSettingKey 补常量），
  这个页面不需要改动。

  ⚠️ 管理端只能**改值**，不能增删键 —— 设置键必须先在代码里登记，
     业务侧才认识它，凭空插键只会得到一条没人消费的死配置。

  ⚠️ 凭据类（后端 sensitive=true，如飞书 webhook / 签名密钥）有三条特殊规则：
     1) 接口返回的 value 是**掩码**（`https://open.feishu.cn/...abcd`），不是真实值，
        所以输入框从**空**起步、用密码框，真实值永远不会出现在 DOM 里；
     2) 保存时**留空 = 不修改**（不是清空），清空要走「恢复默认」；
     3) 操作日志只记「已更新」，不记前后值。
-->
<template>
  <div>
    <div class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />配置项
          <span class="adm-card-title-extra">
            共 {{ settingCount }} 项 · 改完立即生效，无需重启服务
          </span>
        </div>
        <div class="adm-head-actions">
          <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="load">
            刷新
          </el-button>
        </div>
      </div>

      <div v-if="!loading && !groups.length" class="adm-empty">
        <i class="el-icon-setting" />暂无系统设置
      </div>

      <div v-for="group in groups" :key="group.group" class="adm-group">
        <div class="adm-group-head">
          <span class="adm-group-title">{{ group.label }}</span>
          <span class="adm-code">{{ group.group }}</span>
        </div>

        <div v-for="item in group.items" :key="item.key" class="adm-setting">
          <div class="adm-setting-main">
            <div class="adm-setting-head">
              <span class="adm-setting-label">{{ item.label }}</span>
              <el-tag v-if="item.publicVisible" size="mini" type="info">公开</el-tag>
              <el-tag v-if="item.sensitive" size="mini" type="warning">凭据</el-tag>
              <el-tag v-if="item.usingDefault" size="mini" type="success">默认值</el-tag>
              <el-tag
                size="mini"
                :type="item.effectiveMode === 'RESTART' ? 'warning' : 'info'"
              >{{ item.effectiveMode === 'RESTART' ? '重启生效' : '立即生效' }}</el-tag>
            </div>
            <div class="adm-code">{{ item.key }}</div>
            <div v-if="item.remark" class="adm-setting-remark">{{ item.remark }}</div>
            <div class="adm-setting-meta">
              {{ item.valueType }} · 最后修改 {{ formatTime(item.updatedAt) }}
              <template v-if="item.updatedBy === 0">（运维/迁移）</template>
            </div>
            <div v-if="item.sensitive" class="adm-setting-warn">
              <i class="el-icon-lock" />凭据类：页面只显示掩码，保存留空表示不修改，操作日志不记录取值
            </div>
          </div>

          <div class="adm-setting-control">
            <template v-if="item.valueType === 'BOOL'">
              <span class="adm-switch-text">{{ item.checked ? '已开启' : '已关闭' }}</span>
              <el-switch v-model="item.checked" @change="onToggle(item)" />
            </template>
            <template v-else>
              <!--
                凭据类用密码框 + 空值起步：真实值根本不下来，输入框里只有占位提示。
                autocomplete 必须写 new-password —— 否则浏览器会把它当成登录密码框自动填充。
              -->
              <el-input
                v-model="item.editValue"
                class="adm-value-input"
                :name="item.key"
                :type="item.sensitive ? 'password' : 'text'"
                :placeholder="item.sensitive ? sensitivePlaceholder(item) : ''"
                :autocomplete="item.sensitive ? 'new-password' : 'off'"
                @keyup.enter.native="onSave(item)"
              />
              <el-button
                size="small"
                type="primary"
                :loading="item.saving"
                :disabled="!canSave(item)"
                @click="onSave(item)"
              >保存</el-button>
            </template>

            <!--
              「历史 / 恢复默认」按钮槽位。
              「恢复默认」只在非默认值行显示，但**必须占位**——否则带「恢复默认」的行
              会把「历史」往左挤，「历史」在不同行的 x 位置就不一致了。
              占位用的是同款 el-button + visibility:hidden（而不是 span）：span 拿不到
              el-button 的 padding/字号，实测会窄 12px，占位等于白占。
            -->
            <div class="adm-setting-reset">
              <el-button
                size="mini"
                type="text"
                @click="onShowHistory(item)"
              >历史</el-button>
              <el-button
                v-if="!item.usingDefault"
                size="mini"
                type="text"
                @click="onReset(item)"
              >恢复默认</el-button>
              <el-button
                v-else
                size="mini"
                type="text"
                class="adm-setting-reset-placeholder"
                tabindex="-1"
                aria-hidden="true"
              >恢复默认</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 设置项变更历史 -->
    <el-dialog
      title="变更历史"
      :visible.sync="historyVisible"
    width="560px"
    append-to-body
  >
    <div v-if="historyItem" class="adm-history-title">
      {{ historyItem.label }} <span class="adm-code">{{ historyItem.key }}</span>
    </div>
    <div v-if="historyLoading" class="adm-empty"><i class="el-icon-loading" /> 加载中…</div>
    <div v-else-if="!historyList.length" class="adm-empty">
      <i class="el-icon-document" />暂无变更记录
    </div>
    <ul v-else class="adm-history-list">
      <li v-for="(h, i) in historyList" :key="i" class="adm-history-item">
        <div class="adm-history-row">
          <el-tag size="mini">{{ actionText(h.action) }}</el-tag>
          <span class="adm-history-admin">{{ h.adminName || '系统' }}</span>
          <span class="adm-history-time">{{ formatTime(h.createdAt) }}</span>
        </div>
        <div v-if="h.detail" class="adm-history-detail">{{ h.detail }}</div>
        <div v-if="h.ip" class="adm-history-ip">IP：{{ h.ip }}</div>
      </li>
    </ul>
    </el-dialog>
  </div>
</template>

<script>
import { fetchSettings, updateSetting, resetSetting, fetchSettingHistory, isOk } from '@/api/admin'

/**
 * 高危开关的二次确认文案。
 *
 * 只有「会让用户用不了产品」的方向才写在这里；其余开关走通用确认。
 * 后端加了新开关而这里没登记，不会出问题 —— 会退回通用文案。
 */
const DANGER_HINTS = {
  'auth.login.enabled': {
    off: '关闭后所有用户都无法登录。管理端登录是独立账号体系，不受该开关影响，'
      + '你仍可以从这里重新开启。'
  },
  'auth.register.enabled': {
    off: '关闭后新用户无法注册，同时会停止发送注册场景的验证码。'
  },
  'auth.invite_code_required': {
    on: '开启后注册必须携带一枚有效邀请码，没有邀请码的新用户将被拦在门外。'
  }
}

export default {
  name: 'SettingsPanel',
  data() {
    return {
      groups: [],
      loading: false,
      historyVisible: false,
      historyLoading: false,
      historyItem: null,
      historyList: []
    }
  },
  computed: {
    settingCount() {
      return this.groups.reduce((sum, group) => sum + (group.items ? group.items.length : 0), 0)
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      fetchSettings()
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '加载失败')
          }
          this.groups = (res.data || []).map(group => ({
            group: group.group,
            label: group.label || group.group,
            items: (group.items || []).map(this.decorate)
          }))
        })
        .catch(err => {
          this.$message.error(err.message || '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    /**
     * 把后端返回的原始项补齐成可编辑对象。
     * ⚠️ Vue 2 只有初始化时就存在的字段才是响应式的，所以这里必须把
     *    后续会改的字段（value / checked / editValue / saving …）全部声明出来，
     *    否则「第一次能用、之后点不动」。
     */
    decorate(raw) {
      const value = raw.value === null || raw.value === undefined ? '' : String(raw.value)
      const sensitive = !!raw.sensitive
      return {
        id: raw.id,
        key: raw.key,
        label: raw.label,
        remark: raw.remark,
        valueType: raw.valueType || 'STRING',
        group: raw.group,
        publicVisible: !!raw.publicVisible,
        sensitive,
        defaultValue: raw.defaultValue,
        usingDefault: !!raw.usingDefault,
        effectiveMode: raw.effectiveMode || 'IMMEDIATE',
        updatedAt: raw.updatedAt,
        updatedBy: raw.updatedBy,
        value,
        checked: value === 'true',
        // 凭据类：value 是后端给的掩码，不能拿来编辑，输入框从空开始
        editValue: sensitive ? '' : value,
        saving: false
      }
    },
    /** 接口返回的最新值回填到行上（值、默认值标记、修改时间都可能变） */
    applyItem(item, data) {
      if (!data) return
      item.value = data.value === null || data.value === undefined ? '' : String(data.value)
      item.checked = item.value === 'true'
      item.editValue = item.sensitive ? '' : item.value
      item.defaultValue = data.defaultValue
      item.usingDefault = !!data.usingDefault
      item.effectiveMode = data.effectiveMode || 'IMMEDIATE'
      item.updatedAt = data.updatedAt
      item.updatedBy = data.updatedBy
    },
    /**
     * 能否保存。
     *
     * 凭据类的判定刻意与普通项相反：普通项是「值没变就不给存」，
     * 凭据类是「输入框为空就不给存」—— 因为输入框空表示「不修改」，
     * 而不是「把凭据清空」（清空走「恢复默认」）。
     */
    canSave(item) {
      if (item.saving) return false
      if (item.sensitive) return !!item.editValue
      return item.editValue !== item.value
    },
    /** 凭据类输入框的占位提示：把当前掩码显示出来，运维能确认「改没改过」 */
    sensitivePlaceholder(item) {
      if (!item.value) return '当前未配置，粘贴后保存'
      return '已配置（' + item.value + '），留空表示不修改'
    },
    /**
     * 开关切换。
     *
     * el-switch 是 v-model，`@change` 触发时 item.checked 已经是新值，
     * 所以取消/失败都要手动翻回去 —— 否则界面会停在一个并没有保存成功的状态。
     */
    onToggle(item) {
      const target = item.checked
      const hint = (DANGER_HINTS[item.key] || {})[target ? 'on' : 'off']
      const action = target ? '开启' : '关闭'

      this.$confirm(hint || `确定${action}「${item.label}」吗？`, `${action}${item.label}`, {
        confirmButtonText: `确定${action}`,
        cancelButtonText: '取消',
        type: hint ? 'warning' : 'info'
      })
        .then(() => updateSetting(item.key, String(target)))
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '保存失败')
          }
          this.applyItem(item, res.data)
          this.$message.success('已保存')
        })
        .catch(err => {
          item.checked = !target
          if (err === 'cancel' || err === 'close') return
          this.$message.error((err && err.message) || '保存失败')
        })
    },
    onSave(item) {
      if (!this.canSave(item)) return
      item.saving = true
      updateSetting(item.key, item.editValue)
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '保存失败')
          }
          this.applyItem(item, res.data)
          this.$message.success(item.sensitive ? '已保存（输入框已清空，掩码已更新）' : '已保存')
        })
        .catch(err => {
          this.$message.error((err && err.message) || '保存失败')
        })
        .finally(() => {
          item.saving = false
        })
    },
    onReset(item) {
      // 凭据类的默认值就是空串（= 回退到环境变量），直接念「恢复为默认值」会让人以为要写回什么
      const action = item.sensitive
        ? '清空该凭据（清空后会回退到环境变量配置）'
        : `恢复为默认值「${this.displayValue(item, item.defaultValue)}」`
      this.$confirm(`将「${item.label}」${action}？`, '恢复默认值', {
        confirmButtonText: '恢复',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => resetSetting(item.key))
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '恢复失败')
          }
          this.applyItem(item, res.data)
          this.$message.success('已恢复默认值')
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return
          this.$message.error((err && err.message) || '恢复失败')
        })
    },
    displayValue(item, value) {
      if (item.valueType !== 'BOOL') return value
      return String(value) === 'true' ? '开启' : '关闭'
    },
    onShowHistory(item) {
      this.historyItem = item
      this.historyVisible = true
      this.historyLoading = true
      this.historyList = []
      fetchSettingHistory(item.key)
        .then(res => {
          if (!isOk(res)) throw new Error(res && res.message ? res.message : '加载失败')
          this.historyList = (res.data || []).map(h => ({
            adminName: h.adminName,
            action: h.action,
            detail: h.detail,
            ip: h.ip,
            createdAt: h.createdAt
          }))
        })
        .catch(err => {
          this.$message.error(err.message || '加载失败')
        })
        .finally(() => {
          this.historyLoading = false
        })
    },
    actionText(action) {
      if (action === 'SETTING_RESET') return '恢复默认'
      if (action === 'SETTING_UPDATE') return '修改'
      return action || ''
    },
    formatTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 16)
    }
  }
}
</script>

<style scoped>
/* 卡片样式统一来自 @/styles/admin.css，这里只定义「设置分组 / 设置行」 */
.adm-group {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.adm-group:first-of-type {
  border-top: none;
}

.adm-group-head {
  display: flex;
  align-items: baseline;
  padding: 12px 20px;
  background: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.adm-group-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.adm-group-head .adm-code {
  margin-left: 10px;
  color: rgba(0, 0, 0, 0.35);
}

.adm-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.adm-setting:last-child {
  border-bottom: none;
}

.adm-setting:hover {
  background: rgba(0, 0, 0, 0.01);
}

.adm-setting-main {
  min-width: 0;
}

.adm-setting-head {
  display: flex;
  align-items: center;
}

.adm-setting-label {
  margin-right: 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.88);
}

.adm-setting-head .el-tag {
  margin-right: 6px;
}

.adm-setting-remark {
  margin-top: 6px;
  max-width: 640px;
  font-size: 12px;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.55);
}

.adm-setting-meta {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.35);
}

/* 凭据类提示：用警示色，避免运维在不知情的情况下把 webhook 覆盖掉 */
.adm-setting-warn {
  margin-top: 6px;
  font-size: 12px;
  color: #d46b08;
}

.adm-setting-warn i {
  margin-right: 4px;
}

.adm-setting-control {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.adm-switch-text {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}

.adm-value-input {
  width: 240px;
}

/*
 * 固定宽度的按钮槽位：保证「历史」在所有行 x 位置一致。
 *
 * 134px = 「历史」(~70px, el-button--text--mini 实测带 6px 左右内边距)
 *       + 12px gap + 「恢复默认」(~52px 文字) 再加余量，避免字体差异导致换行。
 * 槽位定宽后 flex-end 会把按钮组整体推右，屏幕变窄也不会让按钮互相挤位。
 */
.adm-setting-reset {
  flex: 0 0 134px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

/*
 * 占位按钮：就是同款 el-button，只是不可见、不响应点击、不进 Tab 焦点。
 * 用 visibility 而不是 display:none —— 后者会把它从布局里摘掉，占位就失效了。
 */
.adm-setting-reset-placeholder {
  visibility: hidden;
  pointer-events: none;
  cursor: default;
}

/* 变更历史弹窗 */
.adm-history-title {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.88);
  margin-bottom: 12px;
}

.adm-history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 420px;
  overflow-y: auto;
}

.adm-history-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.adm-history-item:last-child {
  border-bottom: none;
}

.adm-history-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.adm-history-admin {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}

.adm-history-time {
  margin-left: auto;
  font-size:12px;
  color: rgba(0, 0, 0, 0.35);
}

.adm-history-detail {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.65);
  word-break: break-all;
}

.adm-history-ip {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.35);
}

/* 卡片头部的按钮组：包一层，否则 space-between 会把按钮拆到两端 */
.adm-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.adm-head-actions .el-button + .el-button {
  margin-left: 0;
}

@media (max-width: 900px) {
  .adm-setting {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>
